/**
 * Редактор отбора.
 *
 * Одно правило — одна строка: поле, оператор, значение. Строки нумерованы,
 * и связь между ними записывается выражением «(1 И 2) ИЛИ 3»: так в оригинале,
 * и так понятнее вложенных рамок на экране.
 *
 * Браузер не разбирает и не толкует отбор: он собирает список правил и строку
 * связи, а дерево из них строит сервер — той же функцией, которой потом читает.
 * Второй разбор того же языка неизбежно разошёлся бы с первым.
 *
 * Словарь полей и операторов тоже приходит с сервера: новый тип поля появляется
 * в отборе сам, без правки этого файла.
 */

import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";

import type { Condition, FilterField } from "@/shared/api/views";
import { filterVocabulary } from "@/shared/api/views";
import * as metamodel from "@/shared/api/metamodel";
import * as work from "@/shared/api/work";
import { Button, IconButton } from "@/shared/ui/Button";
import { Checkbox } from "@/shared/ui/Choice";
import { Select } from "@/shared/ui/Select";
import { TextField } from "@/shared/ui/TextField";

/** Операторы без значения: строка правила заканчивается на операторе. */
const NO_VALUE = new Set(["is_empty", "is_not_empty", "changed"]);

/** Операторы, которым нужен список: значения вводятся через запятую. */
const MANY_VALUES = new Set(["in", "not_in", "between"]);

const DATE_HINTS = ["@today", "@week.start", "@week.end", "@month.start", "@month.end"];
const PEOPLE_HINTS = ["@me", "@my.department", "@my.groups"];
/** Подстановка «я» осмысленна там, где значение — человек, а не любая ссылка. */
const PEOPLE_FIELDS = new Set(["assignee", "assigner", "participant", "auditor"]);

function hintsFor(field: FilterField): string[] | undefined {
  if (field.kind === "date") {
    return DATE_HINTS;
  }
  return PEOPLE_FIELDS.has(field.name) ? PEOPLE_HINTS : undefined;
}

/** Отбор в том виде, в каком его отправляют серверу. */
export interface FilterDraft {
  conditions: Condition[];
  logic: string;
}

export const EMPTY_FILTER: FilterDraft = { conditions: [], logic: "" };

export function isEmptyFilter(draft: FilterDraft): boolean {
  return draft.conditions.length === 0;
}

/** Связь по умолчанию: все правила разом. */
export function allOf(count: number): string {
  return Array.from({ length: count }, (_, index) => String(index + 1)).join(" И ");
}

/** Оператор по умолчанию: «равно» понятнее, чем первый по алфавиту. */
function defaultOperator(field: FilterField): string {
  for (const preferred of ["eq", "contains", "in", "changed"]) {
    if (field.operators.includes(preferred)) {
      return preferred;
    }
  }
  return field.operators[0] ?? "eq";
}

/** Сколько значений просит оператор: ни одного, одно или список. */
function arity(op: string): "none" | "one" | "many" {
  if (NO_VALUE.has(op)) {
    return "none";
  }
  return MANY_VALUES.has(op) ? "many" : "one";
}

/** Значение переживает смену оператора, если тот просит то же самое:
 * «содержит Иванов» -> «начинается с Иванов» набирать заново незачем. */
function keepValue(previous: string, next: string, value: unknown): unknown {
  return arity(previous) === arity(next) ? value : null;
}

/** Готовые правила: у оператора со значением значение введено.
 *
 * Пока человек набирает, недописанное правило нельзя отправлять на сервер —
 * он ответит ошибкой на каждую букву. Связь при этом пересчитывается заново:
 * номера в выражении относились к полному списку.
 */
export function usable(draft: FilterDraft): FilterDraft {
  const ready = draft.conditions.filter(
    (item) => arity(item.op) === "none" || !isBlank(item.value),
  );
  if (ready.length === draft.conditions.length) {
    return draft;
  }
  return { conditions: ready, logic: allOf(ready.length) };
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  return Array.isArray(value) && (value.length === 0 || value.every(isBlank));
}

export interface FilterEditorProps {
  value: FilterDraft;
  onChange: (value: FilterDraft) => void;
  objectId?: number;
}

export function FilterEditor({ value, onChange, objectId }: FilterEditorProps): React.ReactElement {
  const vocabulary = useQuery({
    queryKey: ["filter-fields", objectId ?? null],
    queryFn: () => filterVocabulary(objectId),
  });
  const statuses = useQuery({ queryKey: ["statuses"], queryFn: () => work.listStatuses() });
  const people = useQuery({ queryKey: ["people"], queryFn: work.listPeople });

  // Связь считается сама, пока человек не взялся за неё руками
  const [manual, setManual] = React.useState(
    value.logic !== "" && value.logic !== allOf(value.conditions.length),
  );

  const fields = React.useMemo(() => vocabulary.data?.fields ?? [], [vocabulary.data]);
  const fieldMap = React.useMemo(() => new Map(fields.map((item) => [item.name, item])), [fields]);
  const operatorTitles = React.useMemo(
    () => new Map((vocabulary.data?.operators ?? []).map((item) => [item.key, item.title])),
    [vocabulary.data],
  );

  const publish = (conditions: Condition[], logic?: string): void => {
    onChange({
      conditions,
      logic: logic ?? (manual ? value.logic : allOf(conditions.length)),
    });
  };

  const addRule = (): void => {
    const first = fields[0];
    if (!first) {
      return;
    }
    publish([...value.conditions, { field: first.name, op: defaultOperator(first), value: null }]);
  };

  const updateRule = (index: number, patch: Partial<Condition>): void => {
    publish(
      value.conditions.map((item, position) => (position === index ? { ...item, ...patch } : item)),
    );
  };

  const removeRule = (index: number): void => {
    const rest = value.conditions.filter((_, position) => position !== index);
    // Номера в выражении сдвинулись бы: после удаления связь считается заново
    publish(rest, allOf(rest.length));
    setManual(false);
  };

  if (vocabulary.isPending) {
    return (
      <p className="text-[var(--pt-fs-sm)] text-[var(--pt-text-secondary)]">Загрузка полей…</p>
    );
  }

  return (
    <div className="flex flex-col gap-[var(--pt-space-12)]">
      <ol className="flex flex-col gap-[var(--pt-space-8)]">
        {value.conditions.map((condition, index) => {
          const field = fieldMap.get(condition.field);
          return (
            <li
              key={`${condition.field}-${String(index)}`}
              className="flex items-center gap-[var(--pt-space-8)]"
            >
              <span className="w-[20px] shrink-0 text-right text-[var(--pt-fs-sm)] text-[var(--pt-text-tertiary)]">
                {index + 1}
              </span>

              <div className="w-[210px] shrink-0">
                <Select
                  ariaLabel={`Поле правила ${String(index + 1)}`}
                  value={condition.field}
                  onValueChange={(name) => {
                    const next = fieldMap.get(name);
                    updateRule(index, {
                      field: name,
                      op:
                        next && next.operators.includes(condition.op)
                          ? condition.op
                          : next
                            ? defaultOperator(next)
                            : "eq",
                      value: null,
                    });
                  }}
                  options={fields.map((item) => ({ value: item.name, label: item.title }))}
                />
              </div>

              <div className="w-[190px] shrink-0">
                <Select
                  ariaLabel={`Условие правила ${String(index + 1)}`}
                  value={condition.op}
                  onValueChange={(op) =>
                    updateRule(index, {
                      op,
                      value: keepValue(condition.op, op, condition.value),
                    })
                  }
                  options={(field?.operators ?? []).map((key) => ({
                    value: key,
                    label: operatorTitles.get(key) ?? key,
                  }))}
                />
              </div>

              <div className="min-w-0 flex-1">
                <ValueInput
                  field={field}
                  condition={condition}
                  index={index}
                  statuses={statuses.data ?? []}
                  people={people.data ?? []}
                  onChange={(next) => updateRule(index, { value: next })}
                />
              </div>

              <IconButton
                label={`Убрать правило ${String(index + 1)}`}
                size="sm"
                icon={<X className="size-[var(--pt-icon)]" />}
                onClick={() => removeRule(index)}
              />
            </li>
          );
        })}
      </ol>

      <div className="flex items-center gap-[var(--pt-space-16)]">
        <Button
          variant="ghost"
          size="sm"
          icon={<Plus className="size-[var(--pt-icon)]" />}
          onClick={addRule}
        >
          Добавить правило
        </Button>

        {value.conditions.length > 1 && (
          <Checkbox
            checked={manual}
            onCheckedChange={(checked) => {
              setManual(checked === true);
              if (checked !== true) {
                publish(value.conditions, allOf(value.conditions.length));
              }
            }}
            label="Своя связь правил"
          />
        )}
      </div>

      {manual && value.conditions.length > 1 && (
        <TextField
          label="Связь правил"
          hint="Номера правил, И, ИЛИ, НЕ и скобки: (1 И 2) ИЛИ 3"
          value={value.logic}
          onChange={(event) => {
            publish(value.conditions, event.target.value);
          }}
        />
      )}
    </div>
  );
}

interface ValueInputProps {
  field: FilterField | undefined;
  condition: Condition;
  index: number;
  statuses: work.Status[];
  people: work.Colleague[];
  onChange: (value: unknown) => void;
}

/** Откуда брать варианты для ссылочного поля отбора. */
const REFERENCE_SOURCES: Record<string, "project" | "object" | "company" | "counterparty"> = {
  project: "project",
  object: "object",
  company: "company",
  counterparty: "counterparty",
};

interface ReferenceValueProps {
  label: string;
  source: "project" | "object" | "company" | "counterparty";
  value: string;
  onChange: (value: string) => void;
}

function ReferenceValue({
  label,
  source,
  value,
  onChange,
}: ReferenceValueProps): React.ReactElement {
  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: () => work.listProjects(),
    enabled: source === "project",
  });
  const objects = useQuery({
    queryKey: ["objects"],
    queryFn: metamodel.listObjects,
    enabled: source === "object",
  });
  const companies = useQuery({
    queryKey: ["companies"],
    queryFn: () => work.listCompanies(),
    enabled: source === "company",
  });
  const counterparties = useQuery({
    queryKey: ["counterparties"],
    queryFn: () => work.listCounterparties(),
    enabled: source === "counterparty",
  });

  const options =
    source === "project"
      ? (projects.data ?? []).map((item) => ({ value: String(item.id), label: item.name }))
      : source === "object"
        ? (objects.data ?? []).map((item) => ({ value: String(item.id), label: item.name }))
        : source === "company"
          ? (companies.data ?? []).map((item) => ({ value: String(item.id), label: item.name }))
          : (counterparties.data ?? []).map((item) => ({
              value: String(item.id),
              label: item.name,
            }));

  return (
    <Select
      ariaLabel={label}
      placeholder="Выберите значение"
      value={value}
      onValueChange={onChange}
      options={options}
    />
  );
}

function asText(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(asText).join(", ");
  }
  if (typeof value === "string") {
    return value;
  }
  // Значение приходит из JSON: показать можно строку, число и «да/нет»
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

function ValueInput({
  field,
  condition,
  index,
  statuses,
  people,
  onChange,
}: ValueInputProps): React.ReactElement | null {
  const label = `Значение правила ${String(index + 1)}`;
  if (!field || NO_VALUE.has(condition.op)) {
    return null;
  }

  const many = MANY_VALUES.has(condition.op);

  if (!many && field.name === "status") {
    return (
      <Select
        ariaLabel={label}
        value={asText(condition.value)}
        onValueChange={(value) => onChange(Number(value))}
        options={statuses.map((status) => ({ value: String(status.id), label: status.name }))}
      />
    );
  }

  if (!many && (field.name === "assignee" || field.name === "assigner")) {
    return (
      <Select
        ariaLabel={label}
        value={asText(condition.value)}
        onValueChange={(value) => onChange(value === "@me" ? value : Number(value))}
        options={[
          { value: "@me", label: "Я" },
          ...people.map((person) => ({ value: String(person.id), label: person.full_name })),
        ]}
      />
    );
  }

  // Ссылочное поле выбирают из списка, а не набирают руками. Написанное
  // название сравнивается с номером записи и не совпадает никогда — человек
  // видит пустой результат и решает, что отбор сломан
  const reference = REFERENCE_SOURCES[field.name];
  if (!many && reference) {
    return (
      <ReferenceValue
        label={label}
        source={reference}
        value={asText(condition.value)}
        onChange={(next) => onChange(next === "" ? "" : Number(next))}
      />
    );
  }

  if (!many && field.kind === "boolean") {
    return (
      <Select
        ariaLabel={label}
        value={asText(condition.value)}
        onValueChange={(value) => onChange(value === "true")}
        options={[
          { value: "true", label: "Да" },
          { value: "false", label: "Нет" },
        ]}
      />
    );
  }

  const hints = hintsFor(field);
  // Дату вводят календарём, а не на память. Раньше здесь стояло обычное поле
  // с подписью «Значение»: в каком виде писать день, приходилось угадывать,
  // а подстановки вроде «@today» набирались только целиком и без подсказки
  const date = !many && field.kind === "date" && !asText(condition.value).startsWith("@");
  return (
    <div className="flex items-center gap-[var(--pt-space-4)]">
      <TextField
        aria-label={label}
        {...(date ? { type: "date" } : {})}
        placeholder={many ? "Значения через запятую" : "Значение"}
        value={asText(condition.value)}
        onChange={(event) => {
          const raw = event.target.value;
          onChange(many ? raw.split(",").map((item) => item.trim()) : raw);
        }}
      />
      {hints && !many && (
        <div className="w-[150px] shrink-0">
          <Select
            ariaLabel={`Подставить значение в правило ${String(index + 1)}`}
            placeholder="Подставить"
            value=""
            onValueChange={(variable) => onChange(variable)}
            options={hints.map((variable) => ({ value: variable, label: variable }))}
          />
        </div>
      )}
    </div>
  );
}
